import React, { useState, useEffect, useContext, useRef } from 'react';
import DistributorCard, { IDistributorCardProps } from '../DistributorCard';
import { useTranslation } from 'react-i18next';
import {
	uniq,
	map,
	groupBy,
	flatMap,
	sortBy,
	toPairs,
	fromPairs,
} from 'lodash';
import ListHeader, { IListHeader } from './ListHeader';
import Pagination from '../Pagination';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import cn from 'classnames';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Loading from 'tbk-components/src/components/Loading';
import { useDebounce } from '../../../hooks';

export interface IDistributorListProps extends IBaseElementProps {
	headerContent?: IListHeader;
	i18n?: {
		filterName?: string;
		searchLabel?: string;
		searchPlaceholder?: string;
		allOptions?: string;
		regionsLabel?: string;
		countriesLabel?: string;
		goldPartnersLabel?: string;
		stockDistributorsLabel?: string;
		noResults?: string;
	};
}

const sortObj = (obj) => {
	const entries = toPairs(obj);
	const sortedEntries = sortBy(entries);
	const sortedObj = fromPairs(sortedEntries);

	return sortedObj;
};

/**
 * Calian Distributor List Component
 * @icon welcome-widgets-menus
 * @block
 */
const DistributorList: React.FC<IDistributorListProps> = withAppContext(
	({ id, className, classNames = [], headerContent, i18n, context }) => {
		const { Api } = useContext(context);
		const { t } = useTranslation();

		const [isLoading, setIsLoading] = useState(true);
		const [regions, setRegions] = useState([]);
		const [countries, setCountries] = useState([]);
		const [sortedDistributors, setSortedDistributors] = useState(null);

		const init = {
			searchQuery: null,
			region: null,
			country: null,
			goldPartner: false,
			stockDistributor: false,
			totalPages: 0,
			currentPage: 1,
		};
		const [state, setState] = useState(init);
		const debouncedSearchQuery = useDebounce(state.searchQuery, 1000);

		// Since there're not many distributors, we can fetch all of them at once
		const perPage = -1;
		const topRef = useRef(null);

		useEffect(() => {
			const args = {
				...(debouncedSearchQuery && {
					s: debouncedSearchQuery,
				}),
				...(state.region && { region: state.region }),
				...(state.country && { country: state.country }),
				...(state.goldPartner && { gold_partner: state.goldPartner }),
				...(state.stockDistributor && {
					stock_distributor: state.stockDistributor,
				}),
				posts_per_page: perPage,
				paged: state.currentPage,
			};

			setIsLoading(true);

			Api.getDistributors(args).then((distributors) => {
				const getUniq = (prop) => {
					return sortBy(
						uniq(
							prop === 'region'
								? map(distributors.results, prop)
								: flatMap(distributors.results, prop),
						),
					);
				};

				// Only populate the regions and countries if they're empty (for the initial render)
				if (regions.length === 0) setRegions(getUniq('region'));
				if (countries.length === 0) setCountries(getUniq('countries'));

				// Display regions in alphabetical order
				setSortedDistributors(
					sortObj(groupBy(distributors.results, 'region')),
				);
				setState({
					...state,
					totalPages: distributors.total_results,
				});
				setIsLoading(false);
			});
		}, [
			debouncedSearchQuery,
			state.region,
			state.country,
			state.goldPartner,
			state.stockDistributor,
			state.currentPage,
		]);

		return (
			<div
				id={id}
				className={
					className ||
					cn('distributor-list__container', ...classNames)
				}
				ref={topRef}
			>
				<div className="distributor-list__container-inner">
					<ListHeader {...headerContent} />
					<div className="distributor-list__filter-bar">
						<div className="distributor-list__filter-bar-inner">
							<button
								className="d-lg-none btn-link"
								type="button"
								data-bs-toggle="collapse"
								data-bs-target="#distributor-list__filters"
								aria-expanded="false"
								aria-controls="distributor-list__filters"
							>
								{i18n?.filterName || t('Distributor Filter')}
								<i className="icon-funnel-simple" />
							</button>
							<form
								id="distributor-list__filters"
								className="collapse d-lg-flex distributor-list__filters"
								onSubmit={(e) => {
									e.preventDefault();
								}}
							>
								<div className="distributor-list__filter-row">
									<label htmlFor="search-distributor">
										{i18n?.searchLabel || t('Search')}
									</label>
									<input
										type="search"
										id="search-distributor"
										name="search-distributor"
										onChange={(e) => {
											setState({
												...state,
												searchQuery: e.target.value,
											});
										}}
										value={state.searchQuery}
										disabled={isLoading}
										placeholder={
											i18n?.searchPlaceholder ||
											'Type to search...'
										}
									/>
								</div>
								<div className="distributor-list__filter-row">
									<label htmlFor="filter-region">
										{i18n?.regionsLabel || t('Regions')}
									</label>
									<select
										name="filter-region"
										id="filter-region"
										onChange={(e) => {
											setState({
												...state,
												region: e.target.value,
											});
										}}
										value={state.region}
										disabled={isLoading}
									>
										<option value="">
											{i18n?.allOptions || t('All')}
										</option>
										{regions.length > 0 &&
											regions.map((region, index) => (
												<option
													key={index}
													value={region.toLowerCase()}
												>
													{region}
												</option>
											))}
									</select>
								</div>
								<div className="distributor-list__filter-row">
									<label htmlFor="filter-country">
										{i18n?.countriesLabel || t('Countries')}
									</label>
									<select
										name="filter-country"
										id="filter-country"
										onChange={(e) => {
											setState({
												...state,
												country: e.target.value,
											});
										}}
										value={state.country}
										disabled={isLoading}
									>
										<option value="">
											{i18n?.allOptions || t('All')}
										</option>
										{countries.length > 0 &&
											countries.map((country, index) => (
												<option
													key={index}
													value={country.toLowerCase()}
												>
													{country}
												</option>
											))}
									</select>
								</div>
								<div className="d-flex align-items-center ms-lg-auto distributor-list__filter-row">
									<input
										type="checkbox"
										id="filter-gold-partners"
										name="filter-gold-partners"
										onChange={(e) => {
											setState({
												...state,
												goldPartner: e.target.checked,
											});
										}}
										disabled={isLoading}
									/>
									<label htmlFor="filter-gold-partners">
										{i18n?.goldPartnersLabel ||
											t('Gold Partners')}
									</label>
								</div>
								<div className="d-flex align-items-center ms-xl-2 me-xl-5 distributor-list__filter-row">
									<input
										type="checkbox"
										id="filter-stock-distributors"
										name="filter-stock-distributors"
										onChange={(e) => {
											setState({
												...state,
												stockDistributor:
													e.target.checked,
											});
										}}
										disabled={isLoading}
									/>
									<label htmlFor="filter-stock-distributors">
										{i18n?.stockDistributorsLabel ||
											t('Stock Distributors')}
									</label>
								</div>
							</form>
						</div>
					</div>
					<div className="distributor-list__listing">
						<div className="distributor-list__listing-inner">
							{isLoading ? (
								<Loading />
							) : (
								<>
									{sortedDistributors &&
									Object.keys(sortedDistributors).length >
										0 ? (
										<>
											{Object.keys(
												sortedDistributors,
											).map((key, index) => (
												<div
													key={index}
													className="distributor-list__listing-by-region"
												>
													<h2 className="h3">
														{key}
													</h2>
													<div className="distributor-list__cards">
														{sortedDistributors[key]
															.length > 0 &&
															sortedDistributors[
																key
															].map(
																(
																	distributor,
																	index,
																) => (
																	<DistributorCard
																		key={
																			index
																		}
																		{...distributor}
																	/>
																),
															)}
													</div>
												</div>
											))}
											<Pagination
												className="pagination-wrapper"
												page={state.currentPage}
												perPage={perPage}
												total={state.totalPages}
												onChange={(page) =>
													setState({
														...state,
														currentPage: page,
													})
												}
												topRef={topRef}
											/>
										</>
									) : (
										<p className="text-center">
											{i18n?.noResults ||
												t('No results found')}
										</p>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	},
);

export default DistributorList;
